import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { DatesProvider } from "dates/DatesContext";
import moment from "moment";
import BreadcrumbDates, { type Breadcrumb } from "components/core/blocks/BreadcrumbDates";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC, DASHBOARD_LINKS } from "utils/dashboardLinks";
import { useModuleEnabled } from "redux/selectors/modules";
import checkTierAccess from "utils/checkTierAccess";
import PaymentDiscovery from "components/custom/onboarding/subscription/PaymentDiscovery";
import EmptyState from "components/custom/merchandise/EmptyState";
import { useAppSelector } from "../../redux/hooks";
import { V4Metrics } from "./components/dashboard/V4Metrics";
import { PageContainer } from "../../components/core/basic/PageContainer";

const getDashboardTo = (category?: string) => {
  return category ? `/user/_pricing/dashboard/category/${category}` : `/user/_pricing/dashboard`;
};

const Dashboard = () => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const user = useAppSelector((state) => state.user);
  const hasAgencyDashboardModule = useModuleEnabled("AGENCY_DASHBOARD");

  const breadCrumbItems: Breadcrumb[] = [
    {
      name: "Pricing Dashboard",
      href: getDashboardTo(),
      dropdownLinkOptions: DASHBOARD_LINKS,
      unsorted: true,
      searchable: false,
      dropdownSubtitle: "Dashboards",
    },
  ];

  if (hasAgencyDashboardModule) {
    breadCrumbItems.unshift(AGENCY_DASHBOARD_BREADCRUMB_SPEC);
  }

  const showEmpty = useMemo(() => {
    const totalSalesStart = user.total_sales_start_date?.[marketPlace.marketPlace];
    if (totalSalesStart) {
      if (moment(totalSalesStart).isBefore(moment().subtract(2, "days"))) {
        return false;
      } else {
        return "COLLECTING_DATA";
      }
    }
    return "NO_DATA";
  }, [user, marketPlace.marketPlace]);

  if (showEmpty) {
    return <EmptyState type={showEmpty} />;
  }

  return (
    <>
      <BreadcrumbDates items={breadCrumbItems} hideCompare hideGroupBy></BreadcrumbDates>
      <PageContainer withBreadcrumb padding={"large"}>
        <V4Metrics />
      </PageContainer>
    </>
  );
};

const PricingDashboardV4 = () => {
  const hasPriceOptimizationModule = useModuleEnabled("PRICE_OPTIMIZATION");

  const user = useAppSelector((state) => state.user);
  const amz_onboard = useAppSelector((state) => state.amz_onboard);

  const location = useLocation<{ firstVisit?: boolean; onboardingComplete?: boolean } | undefined>();

  const DYNAMIC_PRICING_SUBSCRIPTION =
    (((user.pricing_tier && user.pricing_tier !== "unknown") ||
      location.state?.firstVisit ||
      location.state?.onboardingComplete) &&
      (amz_onboard.org_status === "active" ||
        amz_onboard.org_status === "trial_active" ||
        amz_onboard.org_status === "demo_org")) ||
    hasPriceOptimizationModule ||
    checkTierAccess(user.tier, "pricing", amz_onboard.org_status);

  if (!DYNAMIC_PRICING_SUBSCRIPTION) {
    // Workaround for untyped component incorrect inference
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return <PaymentDiscovery discoveryType={"advertising"} dashboardType="pricing" {...({} as any)} />;
  }

  return (
    <DatesProvider>
      <Dashboard />
    </DatesProvider>
  );
};

export default PricingDashboardV4;
